/**
 * Core UI kit on top of styles/design-system.css: buttons, badges, cards, KPI cards, empty /
 * error / loading states, dialogs, dropdown menus, tabs, pager, toasts. Everything is a plain
 * React component with the CSS class names of the design system, so the preview, the printed
 * documents and the app look the same.
 */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { useI18n } from '../lib/i18n.tsx';
import { ApiError } from '../lib/api.ts';

// ----------------------------------------------------------------------------- buttons
export function Button({ variant = 'default', size, icon, busy, children, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'destructive' | 'ghost'; size?: 'sm'; icon?: string; busy?: boolean }) {
  return (
    <button type="button" className={`btn ${variant === 'default' ? '' : variant} ${size ?? ''} ${className}`.trim()} disabled={busy || rest.disabled} {...rest}>
      {busy ? <Icon name="LoaderCircle" className="spin" size={16} /> : icon ? <Icon name={icon} size={16} /> : null}
      {children}
    </button>
  );
}

export function IconButton({ icon, label, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: string; label: string }) {
  return <button type="button" className={`icon-btn ${className}`.trim()} aria-label={label} title={label} {...rest}><Icon name={icon} /></button>;
}

// ----------------------------------------------------------------------------- badges & status
export const STATUS_COLORS: Record<string, string> = {
  draft: 'gray', pending_approval: 'amber', pending: 'amber', submitted: 'amber', approved: 'green', active: 'green', posted: 'green', paid: 'green', completed: 'green', closed: 'gray', done: 'green', delivered: 'green', confirmed: 'green', accepted: 'green', won: 'green', released: 'blue', in_progress: 'blue', sent: 'teal', dispatched: 'teal', open: 'blue', partially_received: 'amber', partially_paid: 'amber', received: 'green', inactive: 'gray', cancelled: 'gray', rejected: 'red', lost: 'red', overdue: 'red', expired: 'red', blocked: 'red', failed: 'red', on_hold: 'amber', qualified: 'blue', new: 'blue', converted: 'green', calculated: 'blue', scheduled: 'blue', locked: 'red', suspended: 'red', void: 'gray', superseded: 'gray',
};

export function Badge({ color, solid, children }: { color?: string | null; solid?: boolean; children: ReactNode }) {
  return <span className={`badge ${color ?? 'gray'} ${solid ? 'solid' : ''}`.trim()}>{children}</span>;
}

export function StatusBadge({ value, options }: { value: unknown; options?: Array<{ value: string; label: string; color: string | null }> | null }) {
  if (value === null || value === undefined || value === '') return <span className="subtle">—</span>;
  const v = String(value);
  const o = options?.find((x) => x.value === v);
  return <Badge color={o?.color ?? STATUS_COLORS[v] ?? 'gray'}>{o?.label ?? v.replace(/_/g, ' ')}</Badge>;
}

// ----------------------------------------------------------------------------- cards
export function Card({ title, actions, children, flush, className = '', icon }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean; className?: string; icon?: string }) {
  return (
    <section className={`card ${className}`.trim()}>
      {title !== undefined && <header className="card-h">{icon && <Icon name={icon} size={18} className="muted" />}<h3>{title}</h3><span className="spacer" />{actions}</header>}
      <div className={`card-b ${flush ? 'flush' : ''}`.trim()}>{children}</div>
    </section>
  );
}

export function KpiCard({ icon, label, value, unit, trend, trendLabel, hint, id }: { icon?: string; label: string; value: ReactNode; unit?: string; trend?: number | null; trendLabel?: string; hint?: string | null; /** stable hook for automated tests (data-kpi) */ id?: string }) {
  const cls = trend === null || trend === undefined ? '' : trend >= 0 ? 'up' : 'down';
  return (
    <div className="card kpi" data-kpi={id}>
      <div className="ico"><Icon name={icon ?? 'Activity'} size={22} /></div>
      <div style={{ minWidth: 0 }}>
        <div className="label">{label}</div>
        <div className="value num">{unit && <small>{unit}</small>}<span data-kpi-value>{value}</span></div>
        {trend !== null && trend !== undefined ? <div className={`trend ${cls}`}><b>{trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%</b><span>{trendLabel}</span></div> : hint ? <div className="trend"><span>{hint}</span></div> : null}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------- states
export function Spinner({ label }: { label?: string }) {
  const { t } = useI18n();
  return <div className="empty"><Icon name="LoaderCircle" className="spin" size={22} /><div style={{ marginTop: 8 }}>{label ?? t('common.loading')}</div></div>;
}

export function EmptyState({ title, hint, action, icon = 'Inbox' }: { title: ReactNode; hint?: ReactNode; action?: ReactNode; icon?: string }) {
  return <div className="empty"><Icon name={icon} size={30} className="subtle" /><b style={{ marginTop: 8 }}>{title}</b>{hint && <div>{hint}</div>}{action && <div style={{ marginTop: 12 }}>{action}</div>}</div>;
}

export function ErrorState({ error, retry }: { error: ApiError | Error | string; retry?: () => void }) {
  const { t } = useI18n();
  const message = typeof error === 'string' ? error : error.message;
  const status = error instanceof ApiError ? error.status : 0;
  const title = status === 403 ? t('errors.forbidden') : status === 404 ? t('errors.notFound') : status === 0 ? t('errors.network') : t('errors.unknown');
  return <div className="banner red-banner"><Icon name="TriangleAlert" /><div><b>{title}</b><div className="muted">{message}</div></div>{retry && <><span className="spacer" /><Button size="sm" onClick={retry} icon="RefreshCw">{t('common.refresh')}</Button></>}</div>;
}

// ----------------------------------------------------------------------------- dialogs
export function Dialog({ open, onClose, title, children, footer, width = 560 }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; width?: number }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current; if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  useEffect(() => {
    const d = ref.current; if (!d) return;
    const onCancel = (e: Event) => { e.preventDefault(); onClose(); };
    d.addEventListener('cancel', onCancel);
    return () => d.removeEventListener('cancel', onCancel);
  }, [onClose]);
  if (!open) return null;
  return (
    <dialog ref={ref} className="dialog" style={{ width: `min(${width}px, 96vw)` }} onClick={(e) => { if (e.target === ref.current) onClose(); }}>
      <div className="dialog-in">
        <header className="dialog-h"><h3>{title}</h3><span className="spacer" /><IconButton icon="X" label="Close" onClick={onClose} /></header>
        <div className="dialog-b">{children}</div>
        {footer && <footer className="dialog-f">{footer}</footer>}
      </div>
    </dialog>
  );
}

/** Confirmation with an optional reason / comment box. Resolves with the text or null when cancelled. */
export function ReasonDialog({ open, onClose, onConfirm, title, message, requireText, label, confirmLabel, destructive, busy }: { open: boolean; onClose: () => void; onConfirm: (text: string) => void; title: ReactNode; message?: ReactNode; requireText?: boolean; label?: string; confirmLabel?: string; destructive?: boolean; busy?: boolean }) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  useEffect(() => { if (open) setText(''); }, [open]);
  const ok = !requireText || text.trim().length > 0;
  return (
    <Dialog open={open} onClose={onClose} title={title} footer={<><Button onClick={onClose}>{t('common.cancel')}</Button><Button variant={destructive ? 'destructive' : 'primary'} data-confirm="reason" disabled={!ok} busy={busy} onClick={() => onConfirm(text.trim())}>{confirmLabel ?? t('common.confirm')}</Button></>}>
      {message && <p className="muted" style={{ marginTop: 0 }}>{message}</p>}
      <div className="field"><label>{label ?? t('common.reason')}{requireText && <span className="req"> *</span>}</label><textarea className="input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('common.reasonPlaceholder')} autoFocus /></div>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------- dropdown menu
export function Menu({ trigger, items, align = 'end' }: { trigger: ReactNode; items: Array<{ label: ReactNode; icon?: string; onClick?: () => void; href?: string; disabled?: boolean; danger?: boolean; hint?: string; /** stable hook for automated tests (data-id) */ id?: string } | 'sep'>; align?: 'start' | 'end' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div className="menu-wrap" ref={ref}>
      <span className="menu-trigger" style={{ display: 'contents' }} onClick={() => setOpen((o) => !o)}>{trigger}</span>
      {open && (
        <div className={`menu ${align}`} role="menu">
          {items.map((it, i) => it === 'sep' ? <div key={i} className="menu-sep" /> : (
            <button key={i} type="button" role="menuitem" className={`menu-item ${it.danger ? 'danger' : ''}`} disabled={it.disabled} title={it.hint} data-id={it.id} onClick={() => { setOpen(false); if (it.href) window.open(it.href, '_blank'); else it.onClick?.(); }}>
              {it.icon && <Icon name={it.icon} size={16} />}<span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------- tabs & pager
export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: Array<{ value: T; label: ReactNode; count?: number | null }> }) {
  return (
    <nav className="tabs">
      {tabs.map((tab) => <a key={tab.value} href="#" className={tab.value === value ? 'active' : ''} onClick={(e) => { e.preventDefault(); onChange(tab.value); }}>{tab.label}{tab.count !== null && tab.count !== undefined && <span className="count">{tab.count}</span>}</a>)}
    </nav>
  );
}

export function Pager({ page, pageSize, total, onPage, onPageSize }: { page: number; pageSize: number; total: number; onPage: (p: number) => void; onPageSize?: (n: number) => void }) {
  const { t } = useI18n();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const around = [page - 2, page - 1, page, page + 1, page + 2].filter((p) => p >= 1 && p <= pages);
  return (
    <div className="table-footer">
      <span>{t('common.showing', { from, to, total })}</span>
      {onPageSize && <select className="input sm" value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>{[10, 25, 50, 100].map((n) => <option key={n} value={n}>{t('common.perPage', { n })}</option>)}</select>}
      <span className="spacer" />
      <div className="pager">
        <a href="#" onClick={(e) => { e.preventDefault(); if (page > 1) onPage(page - 1); }} className={page <= 1 ? 'disabled' : ''}><Icon name="ChevronLeft" size={14} className="rtl-flip" /></a>
        {around[0] > 1 && <a href="#" onClick={(e) => { e.preventDefault(); onPage(1); }}>1</a>}
        {around[0] > 2 && <span className="dots">…</span>}
        {around.map((p) => <a key={p} href="#" className={p === page ? 'active' : ''} onClick={(e) => { e.preventDefault(); onPage(p); }}>{p}</a>)}
        {around[around.length - 1] < pages - 1 && <span className="dots">…</span>}
        {around[around.length - 1] < pages && <a href="#" onClick={(e) => { e.preventDefault(); onPage(pages); }}>{pages}</a>}
        <a href="#" onClick={(e) => { e.preventDefault(); if (page < pages) onPage(page + 1); }} className={page >= pages ? 'disabled' : ''}><Icon name="ChevronRight" size={14} className="rtl-flip" /></a>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------- toasts
interface Toast { id: number; kind: 'success' | 'error' | 'info'; text: string }
const ToastContext = createContext<{ push: (kind: Toast['kind'], text: string) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts, { id, kind, text }]);
    setTimeout(() => setToasts((ts) => ts.filter((t) => t.id !== id)), kind === 'error' ? 7000 : 3500);
  }, []);
  const value = useMemo(() => ({ push }), [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite">{toasts.map((t) => <div key={t.id} className={`toast ${t.kind}`}><Icon name={t.kind === 'success' ? 'CircleCheck' : t.kind === 'error' ? 'CircleAlert' : 'Info'} size={16} />{t.text}</div>)}</div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const v = useContext(ToastContext);
  if (!v) throw new Error('useToast outside ToastProvider');
  return { success: (text: string) => v.push('success', text), error: (e: unknown) => v.push('error', e instanceof Error ? e.message : String(e)), info: (text: string) => v.push('info', text) };
}

// ----------------------------------------------------------------------------- misc
export function Field({ label, children, span, hint, error, required, id }: { label: ReactNode; children: ReactNode; span?: number; hint?: string | null; error?: string | null; required?: boolean; id?: string }) {
  const auto = useId();
  return (
    <div className={`field ${span === 2 ? 'span-2' : ''} ${error ? 'has-error' : ''}`.trim()}>
      <label htmlFor={id ?? auto}>{label}{required && <span className="req"> *</span>}</label>
      {children}
      {error ? <div className="field-error">{error}</div> : hint ? <div className="field-hint">{hint}</div> : null}
    </div>
  );
}

export function Kv({ items }: { items: Array<{ label: ReactNode; value: ReactNode }> }) {
  return <div className="fields">{items.map((it, i) => <div className="field" key={i}><label>{it.label}</label><div className="v">{it.value ?? '—'}</div></div>)}</div>;
}
